/** Admin detail collapse. */
import * as React from 'react';
import { User, Mail, Phone, ShieldUser, Calendar, Building2, Target, MapPin } from 'lucide-react';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailChipList } from '@/components/common/details/DetailChipList';
import { DetailsCard } from '@/components/common/DetailsCard';
import { STAFF_STATUS_CONFIG } from '@/components/admins/admin-configs';
import {
  deriveAdminDetailVisibility,
  mapAdminDepartmentCodeLabels,
} from '@/components/admins/admin-form-utils';
import { getStaffStatusColor } from '@/utils/common-utils';
import { useRoleConfig } from '@/hooks/useRoleConfig';
import { useDepartments } from '@/hooks/useDepartments';
import type { Admin } from '@/types/admin.types';
import { normalizeStaffAccountStatus } from '@/types/user.types';
import { rankedContactDetailFields } from '@/utils/user-contact-utils';

/** Expanded admin detail collapse. */
export const renderAdminDetails = (admin: Admin): React.ReactNode => {
  return <AdminContent admin={admin} />;
};

/** Admin detail body: role category, in-person / remote scope, contact, departments. */
const AdminContent: React.FC<{ admin: Admin }> = ({ admin }) => {
  const roleConfig = useRoleConfig();
  const statusKey = normalizeStaffAccountStatus(String(admin.status));
  const statusConfig = STAFF_STATUS_CONFIG[statusKey] || STAFF_STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;

  const { data: departments = [] } = useDepartments('FR');

  const { showResidence, showEstablishments, showRemoteDepts, showProspectionGoal } =
    deriveAdminDetailVisibility(admin);

  const managedEstablishments = admin.userEstablishments?.map((ue) => ue.establishment) || [];

  const departmentLabels = mapAdminDepartmentCodeLabels(
    (admin.userProfiles?.[0] as { departments?: string[] } | undefined)?.departments,
    departments,
  );

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <User className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">
                {admin.firstName} {admin.lastName}
              </h3>
              <DetailBadge
                label={statusConfig.label}
                icon={StatusIcon}
                colorName={getStaffStatusColor(admin.status)}
                variant="filled"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard icon={User} title="Informations personnelles" colorName="color-2" colSpan={1}>
          <div className="space-y-3">
            {rankedContactDetailFields(admin.primaryContactKind, admin.email, admin.phone).map(
              (field) => (
                <DetailField
                  key={field.label}
                  icon={field.iconKind === 'phone' ? Phone : Mail}
                  label={field.label}
                  value={field.value}
                />
              ),
            )}
          </div>
        </DetailsCard>

        <DetailsCard icon={ShieldUser} title="Rôles et profil" colorName="color-1" colSpan={2}>
          {admin.userRoles && admin.userRoles.length > 0 ? (
            <DetailChipList
              items={admin.userRoles.map((ur) => {
                const config = roleConfig[ur.role.code?.toUpperCase()];
                return {
                  id: ur.roleId,
                  label: config?.label || ur.role.label,
                  icon: config?.icon,
                };
              })}
              colorScheme="color-2"
              variant="filled"
            />
          ) : (
            <EmptyPlaceholder message="Aucun rôle assigné" />
          )}
        </DetailsCard>

        {showProspectionGoal && (
          <DetailsCard
            icon={Target}
            title="Objectif de prospection"
            colorName="color-2"
            colSpan={1}
          >
            <DetailField
              label="Objectif annuel d'établissements à prospecter"
              value={
                admin.userProfiles?.[0]?.annualProspectionGoal != null
                  ? String(admin.userProfiles[0].annualProspectionGoal)
                  : '—'
              }
            />
          </DetailsCard>
        )}

        {showResidence && (
          <DetailsCard icon={MapPin} title="Département" colorName="color-2" colSpan={1}>
            <DetailField
              label="Département(s)"
              value={
                admin.userProfiles?.[0]?.residenceDepartments
                  ?.map((dept) => `${dept.code} — ${dept.name}`)
                  .join(', ') || '—'
              }
            />
          </DetailsCard>
        )}

        {showEstablishments && (
          <DetailsCard
            icon={Building2}
            title="Établissements gérés"
            colorName="color-3"
            colSpan="full"
          >
            {managedEstablishments.length > 0 ? (
              <DetailChipList
                items={managedEstablishments.map((e) => ({
                  id: e.id,
                  label: e.uaiCode ? `${e.name} (${e.uaiCode})` : e.name,
                  icon: Building2,
                }))}
                colorScheme="color-1"
                variant="outlined"
              />
            ) : (
              <EmptyPlaceholder message="Aucun établissement géré" />
            )}
          </DetailsCard>
        )}

        {showRemoteDepts && departmentLabels.length > 0 && (
          <DetailsCard icon={MapPin} title="Département(s)" colorName="color-2" colSpan={1}>
            <DetailChipList
              items={departmentLabels.map((label) => ({ id: label, label, icon: MapPin }))}
              colorScheme="color-2"
              variant="outlined"
            />
          </DetailsCard>
        )}

        {admin.createdAt && (
          <DetailsCard
            icon={Calendar}
            title="Informations système"
            colorName="color-3"
            colSpan="full"
          >
            <div className="grid grid-cols-2 gap-4">
              <DetailField
                label="Créé le"
                value={new Date(admin.createdAt).toLocaleDateString('fr-FR')}
              />
              {admin.updatedAt && (
                <DetailField
                  label="Modifié le"
                  value={new Date(admin.updatedAt).toLocaleDateString('fr-FR')}
                />
              )}
            </div>
          </DetailsCard>
        )}
      </div>
    </>
  );
};
